import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import toast from "react-hot-toast";

export const EnhancedItemsSelector = ({
  open,
  onClose,
  onSelect,
  mainReportItems = null,
  mainReport = null,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("new"); // "new", "main_report"
  const [mainReportMode, setMainReportMode] = useState("all"); // "all"

  useEffect(() => {
    if (open) {
      setMode("new");
      setMainReportMode("all");
    }
  }, [open]);

  const handleMainReportSelect = () => {
    setMode("main_report");
    setMainReportMode("all");
  };

  const handleNewItems = () => {
    setMode("new");
  };

  const handleConfirm = () => {
    if (mode === "new") {
      onSelect({
        mode: "new",
      });
    } else if (mode === "main_report" && mainReportItems && mainReportItems.length > 0) {
      let itemsToCopy = [];
      
      if (mainReportMode === "all") {
        itemsToCopy = mainReportItems;
      }
      
      if (itemsToCopy.length === 0) {
        toast.error("Ingen ordrelinjer å kopiere");
        return;
      }

      onSelect({
        mode: "main_report",
        include_all: mainReportMode === "all",
        items: itemsToCopy,
      });
    } else {
      toast.error("Velg en gyldig opsjon");
    }
  };

  return (
    <Dialog
      as="div"
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 3000 }}
      open={open}
      onClose={onClose}
    >
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900 mb-4"
              >
                Velg ordrelinjer
              </Dialog.Title>

              {isLoading ? (
                <ResourceLoading />
              ) : error ? (
                <ResourceError error={error} />
              ) : (
                <div className="mt-2">
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="items_mode"
                        value="new"
                        checked={mode === "new"}
                        onChange={handleNewItems}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          Ingen ordrelinjer
                        </div>
                        <div className="text-xs text-gray-500">
                          Opprett rapport uten ordrelinjer
                        </div>
                      </div>
                    </label>

                    {mainReportItems && mainReportItems.length > 0 && mainReport && (
                      <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="items_mode"
                          value="main_report"
                          checked={mode === "main_report"}
                          onChange={handleMainReportSelect}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            Kopier fra hovedrapport
                          </div>
                          <div className="text-xs text-gray-500">
                            {mainReportItems.length} ordrelinjer tilgjengelig fra {mainReport.report_number || mainReport.displayTitle || "Hovedrapport"}
                          </div>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* Main Report Options */}
              {mode === "main_report" && mainReport && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Kopierer fra: {mainReport.report_number || mainReport.displayTitle || "Hovedrapport"}
                  </h4>
                  {mainReportItems && mainReportItems.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="main_report_items_mode"
                            value="all"
                            checked={mainReportMode === "all"}
                            onChange={() => setMainReportMode("all")}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Alle ordrelinjer ({mainReportItems.length})
                          </span>
                        </label>
                      </div>

                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs text-gray-600">
                          {mainReportItems.length} ordrelinjer vil bli kopiert
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Hovedrapporten har ingen ordrelinjer å kopiere fra.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleConfirm}
                  disabled={
                    (mode === "main_report" && (!mainReport || !mainReportItems || mainReportItems.length === 0))
                  }
                >
                  Bekreft
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

